<script>
import { reactive, ref, computed, onMounted, inject } from 'vue'
import redeMarketingApi from '@/services/api/redeMarketingRepository'

export default {
  setup() {
    const swal = inject('$swal')
    const ranks = ref([])
    const loading = ref(false)

    const ranksOrdenados = computed(() =>
      [...ranks.value].sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    )

    async function carregar() {
      loading.value = true
      try {
        const resp = await redeMarketingApi.ListarRanks()
        ranks.value = resp.data.map((r) => ({ ...r, _salvando: false }))
      } catch (e) {
        swal({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message })
      } finally {
        loading.value = false
      }
    }

    function novoRank() {
      const proximaOrdem = (ranks.value.reduce((m, r) => Math.max(m, r.ordem || 0), 0)) + 1
      ranks.value.push({
        _tempId: Date.now(),
        id: null,
        codigo: 'NOVO' + proximaOrdem,
        nome: 'Novo Rank',
        ordem: proximaOrdem,
        minCadastrosDiretos: 0,
        minPontosMensais: 0,
        corHex: '#94a3b8',
        ativo: true,
        _salvando: false,
      })
    }

    async function salvar(rank) {
      rank._salvando = true
      try {
        if (rank.id) {
          await redeMarketingApi.AtualizarRank(rank.id, rank)
          swal({ icon: 'success', title: 'Atualizado', text: `Rank "${rank.nome}" salvo.` })
        } else {
          const resp = await redeMarketingApi.CriarRank(rank)
          rank.id = resp.data.id
          delete rank._tempId
          swal({ icon: 'success', title: 'Criado', text: `Rank "${rank.nome}" criado.` })
        }
      } catch (e) {
        swal({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message })
      } finally {
        rank._salvando = false
      }
    }

    async function excluir(rank) {
      swal({
        title: 'Excluir rank?',
        text: `O rank "${rank.nome}" será marcado como inativo.`,
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      }).then(async (r) => {
        if (!r.isConfirmed) return
        if (!rank.id) {
          ranks.value = ranks.value.filter((x) => x._tempId !== rank._tempId)
          return
        }
        try {
          await redeMarketingApi.ExcluirRank(rank.id)
          ranks.value = ranks.value.filter((x) => x.id !== rank.id)
          swal({ icon: 'success', title: 'Excluído', text: `Rank "${rank.nome}" removido.` })
        } catch (e) {
          swal({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message })
        }
      })
    }

    onMounted(carregar)

    return { ranks, ranksOrdenados, loading, novoRank, salvar, excluir }
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="d-flex align-items-center gap-2">
        <router-link to="/rede-marketing/configuracao" class="btn btn-sm btn-light">
          <i class="material-icons align-middle">arrow_back</i>
        </router-link>
        <h3 class="mb-0">Configuração de Ranks</h3>
      </div>
      <button class="btn btn-sm btn-success d-flex align-items-center" @click="novoRank">
        <i class="material-icons me-1" style="font-size: 18px;">add</i>
        Novo Rank
      </button>
    </div>

    <div class="alert alert-info d-flex gap-2 align-items-start">
      <i class="material-icons">info</i>
      <div>
        Configure os níveis (ranks) que seus membros podem atingir a cada mês. Os critérios
        são <strong>cadastros diretos</strong> e <strong>pontos acumulados</strong>. Os ranks
        são recalculados no início de cada mês.
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <div v-else class="d-flex flex-column gap-2">
      <div v-for="rank in ranksOrdenados" :key="rank.id || rank._tempId"
           class="card rank-card" :style="{ borderLeftColor: rank.corHex || '#6c757d' }">
        <div class="card-body">
          <div class="d-flex gap-2 align-items-center flex-wrap mb-2">
            <input v-model.number="rank.ordem" type="number" class="form-control form-control-sm"
                   style="width: 70px;" title="Ordem" />
            <input v-model="rank.nome" type="text" class="form-control form-control-sm flex-1"
                   style="min-width: 160px;" placeholder="Nome" />
            <input v-model="rank.codigo" type="text" class="form-control form-control-sm"
                   style="width: 120px; font-family: monospace;" placeholder="CÓDIGO" />
            <input v-model="rank.corHex" type="color" class="form-control form-control-color form-control-sm"
                   style="width: 40px;" title="Cor" />
            <div class="form-check form-switch ms-2">
              <input class="form-check-input" type="checkbox" v-model="rank.ativo" :id="'sw-'+(rank.id||rank._tempId)" />
              <label class="form-check-label small" :for="'sw-'+(rank.id||rank._tempId)">
                {{ rank.ativo ? 'Ativo' : 'Inativo' }}
              </label>
            </div>
          </div>

          <div class="row g-2 align-items-end">
            <div class="col-md-5">
              <label class="form-label small text-muted">
                <i class="material-icons align-middle" style="font-size: 14px;">person_add</i>
                Cadastros diretos mínimos
              </label>
              <input v-model.number="rank.minCadastrosDiretos" type="number" min="0"
                     class="form-control form-control-sm" />
            </div>
            <div class="col-md-5">
              <label class="form-label small text-muted">
                <i class="material-icons align-middle" style="font-size: 14px;">stars</i>
                Pontos mensais mínimos
              </label>
              <input v-model.number="rank.minPontosMensais" type="number" min="0" step="0.01"
                     class="form-control form-control-sm" />
            </div>
            <div class="col-md-2 d-flex gap-1 justify-content-end">
              <button class="btn btn-sm btn-success d-flex align-items-center"
                      :disabled="rank._salvando" @click="salvar(rank)" title="Salvar">
                <i class="material-icons" style="font-size: 18px;">save</i>
              </button>
              <button class="btn btn-sm btn-outline-danger d-flex align-items-center"
                      @click="excluir(rank)" title="Excluir">
                <i class="material-icons" style="font-size: 18px;">delete</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="ranksOrdenados.length === 0" class="text-center py-5 text-muted small">
        Nenhum rank cadastrado. Clique em <strong>"Novo Rank"</strong> para começar.
      </div>
    </div>
  </div>
</template>

<style scoped>
.rank-card {
  border-left: 5px solid #6c757d;
}
.flex-1 { flex: 1; }
</style>
